import {
  parse,
  print,
  visit,
  FragmentDefinitionNode,
  SelectionSetNode,
  SelectionNode,
  DefinitionNode,
  FieldNode,
  InlineFragmentNode,
  Kind,
} from 'graphql'

export function inlineFragments(queryStr: string): string {
  const ast = parse(queryStr)
  const fragments: Record<string, FragmentDefinitionNode> = {}

  // collect all fragment definitions
  for (const def of ast.definitions) {
    if (def.kind === 'FragmentDefinition') {
      fragments[def.name.value] = def
    }
  }

  // merge selections with the same field name
  function mergeSelections(selections: SelectionNode[]): SelectionNode[] {
    const fieldMap = new Map<string, FieldNode[]>()
    const inlineFragments: InlineFragmentNode[] = []
    const otherSelections: SelectionNode[] = []

    // group selections by type and field name
    for (const sel of selections) {
      if (sel.kind === 'Field') {
        const key = sel.alias?.value || sel.name.value
        if (!fieldMap.has(key)) {
          fieldMap.set(key, [])
        }
        fieldMap.get(key)!.push(sel)
      } else if (sel.kind === 'InlineFragment') {
        inlineFragments.push(sel)
      } else {
        otherSelections.push(sel)
      }
    }

    // merge fields with the same name
    const mergedFields: FieldNode[] = []
    for (const [key, fields] of fieldMap) {
      if (fields.length === 1) {
        mergedFields.push(fields[0])
      } else {
        // merge multiple fields with the same name
        const firstField = fields[0]
        const mergedSelections: SelectionNode[] = []

        for (const field of fields) {
          if (field.selectionSet) {
            mergedSelections.push(...field.selectionSet.selections)
          }
        }

        const mergedField: FieldNode = {
          ...firstField,
          selectionSet:
            mergedSelections.length > 0
              ? {
                  kind: Kind.SELECTION_SET,
                  selections: mergeSelections(mergedSelections),
                }
              : undefined,
        }
        mergedFields.push(mergedField)
      }
    }

    // process inline fragments
    const processedInlineFragments = inlineFragments.map(frag => ({
      ...frag,
      selectionSet: {
        ...frag.selectionSet,
        selections: mergeSelections([...frag.selectionSet.selections]),
      },
    }))

    return [...mergedFields, ...processedInlineFragments, ...otherSelections]
  }

  // recursively inline fragment spreads
  function inlineSelectionSet(selectionSet: SelectionSetNode): SelectionSetNode {
    const inlinedSelections = selectionSet.selections.flatMap((sel: SelectionNode) => {
      if (sel.kind === 'FragmentSpread') {
        const frag = fragments[sel.name.value]
        if (!frag) throw new Error(`fragment ${sel.name.value} not found`)
        return inlineSelectionSet(frag.selectionSet).selections
      } else if ('selectionSet' in sel && sel.selectionSet) {
        return [{ ...sel, selectionSet: inlineSelectionSet(sel.selectionSet) }]
      } else {
        return [sel]
      }
    })

    // merge duplicate selections after inlining
    return {
      ...selectionSet,
      selections: mergeSelections(inlinedSelections),
    }
  }

  // process all operation definitions
  const newAst = {
    ...ast,
    definitions: ast.definitions
      .filter((def: DefinitionNode) => def.kind !== 'FragmentDefinition')
      .map((def: DefinitionNode) =>
        'selectionSet' in def && def.selectionSet
          ? { ...def, selectionSet: inlineSelectionSet(def.selectionSet) }
          : def,
      ),
  }

  return print(newAst)
}
